import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Pencil,
  Trash2,
  Users,
} from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import ConfirmDialog from '@/components/ConfirmDialog'
import { formatRelativeTime, formatDate } from '@/lib/displayUtils'
import { sanitizeHtml } from '@/lib/sanitizeHtml'
import { outboundStatus } from '@/lib/outboundStatus'

interface OutboundMessage {
  id: number
  name: string
  message_type: 'chat' | 'post' | 'email'
  post_format: 'small' | 'large' | null
  reply_type: 'none' | 'reactions' | 'text'
  reaction_emojis: string[]
  title: string | null
  body: string
  button_text: string | null
  button_url: string | null
  audience_type: 'dynamic' | 'fixed'
  trigger_type: 'event' | 'scheduled' | 'page' | 'all_pages'
  trigger_event: string | null
  trigger_page_rules: { match_type: string; value: string }[] | null
  scheduled_at: string | null
  frequency_cap: number | null
  priority: number
  status: 'draft' | 'active' | 'paused' | 'completed'
  activated_at: string | null
  expires_at: string | null
  created_at: string
  created_by: { id: number; name: string } | null
}

interface Stats {
  sent: number
  opened: number
  clicked: number
  replied: number
}

interface Delivery {
  id: number
  status: string
  channel: 'chat' | 'email'
  delivered_at: string | null
  seen_at: string | null
  clicked_at: string | null
  replied_at: string | null
  reaction: string | null
  reacted_at: string | null
  end_user: {
    id: number
    name: string
    email: string | null
  }
}

interface ChartData {
  unit: 'day' | 'hour'
  day: string | null
  // True when the hourly view came from a day click and a daily view exists
  drilldown: boolean
  points: { label: string; value: number }[]
}

interface Pagination {
  page: number
  per_page: number
  total: number
  total_pages: number
}

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface ShowProps {
  outbound_message: OutboundMessage
  stats: Stats
  reactions_breakdown: Record<string, number>
  deliveries: Delivery[]
  chart: ChartData | null
  range: { from: string; to: string } | null
  filter: string | null
  pagination: Pagination
  currentAgent: Agent
}

// One entry per metric tab; the key doubles as the backend filter param
const TAB_CONFIG: Record<
  string,
  { timestamp: keyof Delivery; headline: string; whenLabel: string }
> = {
  all: {
    timestamp: 'delivered_at',
    headline: 'clientes receberam esta mensagem.',
    whenLabel: 'Quando recebeu',
  },
  seen: {
    timestamp: 'seen_at',
    headline: 'clientes abriram esta mensagem.',
    whenLabel: 'Quando abriu',
  },
  clicked: {
    timestamp: 'clicked_at',
    headline: 'clientes clicaram em um link ou botão.',
    whenLabel: 'Quando clicou',
  },
  replied: {
    timestamp: 'replied_at',
    headline: 'clientes responderam.',
    whenLabel: 'Quando respondeu',
  },
  reacted: {
    timestamp: 'reacted_at',
    headline: 'clientes reagiram.',
    whenLabel: 'Quando reagiu',
  },
}

function messageTypeLabel(msg: OutboundMessage) {
  if (msg.message_type === 'chat') return 'Chat'
  if (msg.message_type === 'email') return 'E-mail'
  return msg.post_format === 'large' ? 'Post (grande)' : 'Post (pequeno)'
}

function percent(value: number, total: number) {
  return total > 0 ? `${Math.round((value / total) * 100)}%` : '—'
}

function formatNumber(value: number) {
  return value.toLocaleString('pt-BR')
}

function relativeTime(value: string | null) {
  return value ? formatRelativeTime(value) : '-'
}

function formatDateTime(value: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDayLabel(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

function formatFullDay(iso: string) {
  return formatDate(`${iso}T00:00:00`)
}

function isoDate(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Shared chrome for the header "mais" menu and the date-range selector:
// trigger button + click-away overlay + anchored panel
function Dropdown({
  trigger,
  width,
  children,
}: {
  trigger: React.ReactNode
  width: string
  children: (close: () => void) => React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
      >
        {trigger}
        <ChevronDown className="w-4 h-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={close} />
          <div
            className={`absolute right-0 top-full mt-1 ${width} bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-20`}
          >
            {children(close)}
          </div>
        </>
      )}
    </div>
  )
}

interface RangePreset {
  key: string
  label: string
  from: string
  to: string
}

function buildRangePresets(): RangePreset[] {
  const today = new Date()
  const daysAgo = (n: number) => {
    const d = new Date(today)
    d.setDate(d.getDate() - n)
    return d
  }
  const monthsAgo = (n: number) => {
    const d = new Date(today)
    d.setMonth(d.getMonth() - n)
    return d
  }
  // Monday-based offset of today; last week runs Monday through Sunday
  const weekdayOffset = (today.getDay() + 6) % 7

  return [
    { key: 'today', label: 'Hoje', from: isoDate(today), to: isoDate(today) },
    { key: 'yesterday', label: 'Ontem', from: isoDate(daysAgo(1)), to: isoDate(daysAgo(1)) },
    {
      key: 'last_week',
      label: 'Semana passada',
      from: isoDate(daysAgo(weekdayOffset + 7)),
      to: isoDate(daysAgo(weekdayOffset + 1)),
    },
    {
      key: 'month_to_date',
      label: 'Mês até o momento',
      from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: isoDate(today),
    },
    { key: 'last_4_weeks', label: 'Últimas 4 semanas', from: isoDate(daysAgo(27)), to: isoDate(today) },
    { key: 'last_12_weeks', label: 'Últimas 12 semanas', from: isoDate(daysAgo(83)), to: isoDate(today) },
    {
      key: 'year_to_date',
      label: 'Acumulado do ano',
      from: isoDate(new Date(today.getFullYear(), 0, 1)),
      to: isoDate(today),
    },
    { key: 'last_6_months', label: 'Últimos 6 meses', from: isoDate(monthsAgo(6)), to: isoDate(today) },
    { key: 'last_12_months', label: 'Últimos 12 meses', from: isoDate(monthsAgo(12)), to: isoDate(today) },
  ]
}

interface RangeSelectorProps {
  range: { from: string; to: string } | null
  onChange: (range: { from: string; to: string } | null) => void
}

function RangeSelector({ range, onChange }: RangeSelectorProps) {
  const [customMode, setCustomMode] = useState(false)
  const [customFrom, setCustomFrom] = useState(range?.from ?? '')
  const [customTo, setCustomTo] = useState(range?.to ?? '')

  const presets = buildRangePresets()
  const activePreset = range
    ? presets.find((p) => p.from === range.from && p.to === range.to)
    : null

  const label = range
    ? range.from === range.to
      ? formatFullDay(range.from)
      : `${formatFullDay(range.from)} - ${formatFullDay(range.to)}`
    : 'Todo o período'

  return (
    <Dropdown
      width="w-64"
      trigger={
        <>
          <Calendar className="w-4 h-4" />
          {label}
        </>
      }
    >
      {(close) => {
        const select = (value: { from: string; to: string } | null) => {
          close()
          setCustomMode(false)
          onChange(value)
        }

        return (
          <>
            <button
              onClick={() => select(null)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Todo o período
              </span>
              {!range && <Check className="w-4 h-4 text-orange-500" />}
            </button>
            {presets.map((preset) => (
              <button
                key={preset.key}
                onClick={() => select({ from: preset.from, to: preset.to })}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                  activePreset?.key === preset.key ? 'text-orange-600 font-medium' : 'text-gray-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {preset.label}
                </span>
                {activePreset?.key === preset.key && <Check className="w-4 h-4 text-orange-500" />}
              </button>
            ))}
            <div className="border-t border-gray-100 mt-1 pt-1">
              {customMode ? (
                <div className="px-3 py-2 space-y-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    aria-label="Data inicial"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                  />
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    aria-label="Data final"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                  />
                  <button
                    onClick={() => {
                      if (customFrom && customTo && customFrom <= customTo) {
                        select({ from: customFrom, to: customTo })
                      }
                    }}
                    className="w-full px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700"
                  >
                    Aplicar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setCustomMode(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="w-4 h-4" />
                  Personalizado
                </button>
              )}
            </div>
          </>
        )
      }}
    </Dropdown>
  )
}

interface MetricChartProps {
  chart: ChartData
  // When set, values render as a percentage of this total
  percentOf: number | null
  onSelectDay: (day: string) => void
  onBack: () => void
}

// Lightweight inline SVG line chart; clicking a day point drills into hours
function MetricChart({ chart, percentOf, onSelectDay, onBack }: MetricChartProps) {
  const W = 800
  const H = 200
  const PAD_X = 44
  const PAD_Y = 24

  const values = chart.points.map((p) =>
    percentOf && percentOf > 0 ? (p.value / percentOf) * 100 : p.value
  )
  const max = Math.max(...values, 1)
  const count = chart.points.length

  const x = (i: number) => PAD_X + (i * (W - 2 * PAD_X)) / Math.max(count - 1, 1)
  const y = (v: number) => H - PAD_Y - (v / max) * (H - 2 * PAD_Y)

  const formatValue = (value: number) => {
    const v = Math.round(value * 10) / 10
    return percentOf ? `${v}%` : formatNumber(v)
  }

  // At most ~8 x-axis labels to keep them readable
  const labelStep = Math.max(1, Math.ceil(count / 8))

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-900">
          {chart.unit === 'hour' && chart.day
            ? `Por hora em ${formatDayLabel(chart.day)}`
            : 'Como isso mudou com o tempo?'}
        </h3>
        {chart.drilldown && (
          <button
            onClick={onBack}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Voltar para visão diária
          </button>
        )}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Gráfico de evolução">
        {/* y axis guides */}
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line
              x1={PAD_X}
              x2={W - PAD_X}
              y1={y(max * f)}
              y2={y(max * f)}
              stroke="#f3f4f6"
              strokeWidth="1"
            />
            <text x={PAD_X - 8} y={y(max * f) + 4} textAnchor="end" fontSize="10" fill="#9ca3af">
              {formatValue(max * f)}
            </text>
          </g>
        ))}

        <polyline
          fill="none"
          stroke="#2563eb"
          strokeWidth="1.5"
          points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
        />

        {chart.points.map((point, i) => (
          <g key={point.label}>
            <circle
              cx={x(i)}
              cy={y(values[i])}
              r={chart.unit === 'day' ? 4 : 3}
              fill="#2563eb"
              data-testid="chart-point"
              data-label={point.label}
              style={chart.unit === 'day' ? { cursor: 'pointer' } : undefined}
              onClick={chart.unit === 'day' ? () => onSelectDay(point.label) : undefined}
            >
              <title>{`${chart.unit === 'day' ? formatDayLabel(point.label) : point.label}: ${formatValue(values[i])}`}</title>
            </circle>
            {i % labelStep === 0 && (
              <text x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#9ca3af">
                {chart.unit === 'day' ? formatDayLabel(point.label) : point.label}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
      {children}
    </span>
  )
}

export default function OutboundMessageShow({
  outbound_message: msg,
  stats,
  reactions_breakdown,
  deliveries,
  chart,
  range,
  filter,
  pagination,
  currentAgent,
}: ShowProps) {
  const [activating, setActivating] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const navigate = (params: { filter?: string; page?: number; day?: string; from?: string; to?: string }) => {
    router.get(`/outbound_messages/${msg.id}`, params, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  // The selected date range survives tab switches, drill-downs and pagination
  const rangeParams = range ? { from: range.from, to: range.to } : {}

  const hasReactions = msg.reply_type === 'reactions'
  const hasTextReply = msg.reply_type === 'text'
  const status = outboundStatus(msg.status)

  const tabs: { key: string | null; label: string; value: React.ReactNode }[] = [
    { key: null, label: 'Visão geral', value: messageTypeLabel(msg) },
    { key: 'all', label: 'Enviado', value: formatNumber(stats.sent) },
    {
      key: 'seen',
      label: 'Aberto',
      value: stats.sent > 0 ? `${percent(stats.opened, stats.sent)} (${formatNumber(stats.opened)})` : '—',
    },
    {
      key: 'clicked',
      label: 'Clicado',
      value: stats.clicked > 0 ? `${percent(stats.clicked, stats.sent)} (${formatNumber(stats.clicked)})` : '—',
    },
    ...(hasTextReply
      ? [
          {
            key: 'replied',
            label: 'Respostas',
            value: stats.replied > 0 ? `${percent(stats.replied, stats.sent)} (${formatNumber(stats.replied)})` : '—',
          },
        ]
      : []),
    ...(hasReactions
      ? [
          {
            key: 'reacted',
            label: 'Reagiu',
            value: (
              <span className="flex items-center gap-2">
                {Object.entries(reactions_breakdown)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 2)
                  .map(([emoji, count]) => (
                    <span key={emoji} className="flex items-center gap-0.5">
                      <span>{emoji}</span>
                      <span>{percent(count, stats.sent)}</span>
                    </span>
                  ))}
              </span>
            ),
          },
        ]
      : []),
  ]

  const activeTab = filter && TAB_CONFIG[filter] ? filter : null
  const tabConfig = activeTab ? TAB_CONFIG[activeTab] : null

  return (
    <>
      <Head title={`${msg.name} - Mensagens de Saída`} />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="outbound" />

        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Header */}
          <header className="flex items-center justify-between px-6 pt-4">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                href="/outbound_messages"
                className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Voltar"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <h1 className="text-lg font-semibold text-gray-900 truncate">{msg.name}</h1>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${status.badge}`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Dropdown width="w-44" trigger="mais">
                {(close) => (
                  <>
                    <button
                      onClick={() => {
                        close()
                        router.post(`/outbound_messages/${msg.id}/duplicate`)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Copy className="w-4 h-4" />
                      Duplicar
                    </button>
                    <button
                      onClick={() => {
                        close()
                        setDeleting(true)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Excluir
                    </button>
                  </>
                )}
              </Dropdown>
              {msg.status === 'active' ? (
                <button
                  onClick={() => router.post(`/outbound_messages/${msg.id}/pause`)}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Pausar
                </button>
              ) : msg.status !== 'completed' ? (
                <button
                  onClick={() => setActivating(true)}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Ativar
                </button>
              ) : null}
              <Link
                href={`/outbound_messages/${msg.id}/edit`}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-700 transition-colors"
              >
                Editar
              </Link>
            </div>
          </header>

          {/* Meta line */}
          <p className="px-6 mt-2 text-sm text-gray-500">
            {msg.activated_at
              ? `Ativada ${relativeTime(msg.activated_at)}`
              : `Criada ${relativeTime(msg.created_at)}`}
            {msg.created_by ? ` por ${msg.created_by.name}` : ''}
          </p>

          {/* Stats tab bar */}
          <div className="px-6 mt-4 border-b border-gray-200">
            <div className="flex gap-8">
              {tabs.map((tab) => (
                <button
                  key={tab.key ?? 'overview'}
                  onClick={() => navigate(tab.key ? { filter: tab.key, ...rangeParams } : {})}
                  className={`pb-3 text-left border-b-2 -mb-px transition-colors ${
                    activeTab === tab.key
                      ? 'border-orange-500'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <span className="block text-base font-semibold text-gray-900">{tab.value}</span>
                  <span className={`block text-sm ${activeTab === tab.key ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <main className="flex-1 overflow-auto px-6 py-6 bg-gray-50">
            {!tabConfig ? (
              /* ── Overview ─────────────────────────────────────────────── */
              <div className="max-w-4xl space-y-8">
                <section>
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">Conteúdo</h2>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm max-w-xl mx-auto overflow-hidden">
                    <div className="p-6">
                      {msg.title && (
                        <h3 className="text-xl font-bold text-gray-900 text-center mb-4">{msg.title}</h3>
                      )}
                      <div
                        className="text-sm text-gray-700 leading-relaxed [&_p]:mb-3 [&_img]:rounded-lg [&_img]:max-w-full"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.body) }}
                      />
                      {msg.button_text && (
                        <div className="mt-4 text-center">
                          <span className="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg">
                            {msg.button_text}
                          </span>
                        </div>
                      )}
                      {hasReactions && (
                        <div className="mt-5 flex justify-center gap-2">
                          {msg.reaction_emojis.map((emoji) => (
                            <span
                              key={emoji}
                              className="w-10 h-10 flex items-center justify-center text-lg border border-gray-200 rounded-full"
                            >
                              {emoji}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">Regras</h2>
                  <div className="flex flex-wrap gap-2">
                    <Chip>{msg.audience_type === 'dynamic' ? 'Público dinâmico' : 'Público fixo'}</Chip>
                    {msg.trigger_type === 'all_pages' && <Chip>Todas as páginas</Chip>}
                    {msg.trigger_type === 'event' && msg.trigger_event && (
                      <Chip>Evento: {msg.trigger_event}</Chip>
                    )}
                    {msg.trigger_type === 'page' &&
                      (msg.trigger_page_rules || []).map((rule, i) => (
                        <Chip key={i}>
                          URL {rule.match_type} {rule.value}
                        </Chip>
                      ))}
                  </div>
                </section>

                <section>
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">Frequência e agendamento</h2>
                  <div className="flex flex-wrap gap-2">
                    <Chip>Enviar uma vez, na primeira vez que a pessoa corresponder às regras</Chip>
                    {msg.trigger_type === 'scheduled' && msg.scheduled_at ? (
                      <Chip>Agendada para {formatDateTime(msg.scheduled_at)}</Chip>
                    ) : (
                      <Chip>Começar a enviar imediatamente</Chip>
                    )}
                    {msg.expires_at && <Chip>Parar de enviar em {formatDateTime(msg.expires_at)}</Chip>}
                    {msg.frequency_cap && <Chip>Máximo de {msg.frequency_cap} mensagens em 24h</Chip>}
                    {msg.priority > 0 && <Chip>Prioridade {msg.priority}</Chip>}
                  </div>
                </section>
              </div>
            ) : (
              /* ── Metric tab: recipients list ──────────────────────────── */
              <div className="max-w-5xl">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">{formatNumber(pagination.total)}</span>{' '}
                    {tabConfig.headline}
                  </p>
                  <RangeSelector
                    range={range}
                    onChange={(newRange) =>
                      navigate(
                        newRange
                          ? { filter: activeTab!, from: newRange.from, to: newRange.to }
                          : { filter: activeTab! }
                      )
                    }
                  />
                </div>

                {chart && (
                  <MetricChart
                    chart={chart}
                    percentOf={activeTab === 'all' ? null : stats.sent}
                    onSelectDay={(day) => navigate({ filter: activeTab!, day, ...rangeParams })}
                    onBack={() => navigate({ filter: activeTab!, ...rangeParams })}
                  />
                )}

                {deliveries.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <Users className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">Nenhum cliente aqui ainda</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nome
                          </th>
                          {activeTab === 'reacted' && (
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Reação
                            </th>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {tabConfig.whenLabel}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {deliveries.map((delivery) => (
                          <tr key={delivery.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {delivery.end_user.name}
                              </div>
                              {delivery.end_user.email && (
                                <div className="text-xs text-gray-500">{delivery.end_user.email}</div>
                              )}
                            </td>
                            {activeTab === 'reacted' && (
                              <td className="px-4 py-3 whitespace-nowrap text-lg">
                                {delivery.reaction
                                  ? delivery.reaction
                                  : '-'}
                              </td>
                            )}
                            <td
                              className="px-4 py-3 whitespace-nowrap text-sm text-gray-500"
                              title={formatDateTime(delivery[tabConfig.timestamp] as string | null)}
                            >
                              {relativeTime(delivery[tabConfig.timestamp] as string | null)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {pagination.total_pages > 1 && (
                      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Página {pagination.page} de {pagination.total_pages}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => navigate({ filter: activeTab!, page: pagination.page - 1, ...rangeParams })}
                            disabled={pagination.page <= 1}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-40 disabled:pointer-events-none transition-colors"
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate({ filter: activeTab!, page: pagination.page + 1, ...rangeParams })}
                            disabled={pagination.page >= pagination.total_pages}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-40 disabled:pointer-events-none transition-colors"
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      <ConfirmDialog
        open={activating}
        onOpenChange={(open) => !open && setActivating(false)}
        title="Publicar mensagem"
        description={`Tem certeza que deseja publicar "${msg.name}"? A mensagem será enviada aos destinatários.`}
        confirmLabel="Publicar"
        onConfirm={() => {
          router.post(`/outbound_messages/${msg.id}/activate`)
          setActivating(false)
        }}
      />

      <ConfirmDialog
        open={deleting}
        onOpenChange={(open) => !open && setDeleting(false)}
        title="Excluir mensagem"
        description={`Tem certeza que deseja excluir "${msg.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        onConfirm={() => {
          router.delete(`/outbound_messages/${msg.id}`)
          setDeleting(false)
        }}
      />
    </>
  )
}
